import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { X, FileCsv, FilePdf } from "@phosphor-icons/react";
import { api, formatApiError } from "../../../lib/api";
import { SortableTable } from "./SortableTable";
import { inr, rowsToCsv, saveBlob } from "./iaUtils";
import { cellText } from "./columns";
import { plainPdf } from "./pdf";

const pay = (p) => (p ? { pay_state: p.state, pay_date: (p.paid_at || "").slice(0, 10), pay_id: p.payment_id, pay_source: p.source } : {});
const payCols = [
  { key: "pay_date", label: "Paid on" }, { key: "pay_id", label: "Payment ID" }, { key: "pay_source", label: "Source" },
];
const TABS = {
  service_requests: { label: "Service requests", map: (r) => ({ ...r, date: r.created, amount: r.pay.amount, ...pay(r.pay) }),
    cols: [{ key: "date", label: "Date" }, { key: "no", label: "SR No" }, { key: "status", label: "Status" },
      { key: "pay_state", label: "Payment" }, { key: "amount", label: "Amount", align: "right", render: (r) => inr(r.amount) }, ...payCols] },
  invoices: { label: "Invoices", map: (r) => ({ ...r, status: r.paid ? "Paid" : "Unpaid", pay_date: r.paid_on, pay_id: r.payment_id, pay_source: r.source }),
    cols: [{ key: "date", label: "Date" }, { key: "no", label: "Invoice No" }, { key: "status", label: "Status" },
      { key: "amount", label: "Amount", align: "right", render: (r) => inr(r.amount) }, ...payCols] },
  payments: { label: "Payments", map: (r) => ({ ...r, date: r.on, status: r.refunded ? "Refunded" : r.prepaid ? "Prepaid" : "Paid", pay_id: r.payment_id, pay_source: r.source, no: r.ref }),
    cols: [{ key: "date", label: "Date" }, { key: "no", label: "Reference" }, { key: "kind", label: "Type" }, { key: "status", label: "Status" },
      { key: "amount", label: "Amount", align: "right", render: (r) => inr(r.amount) }, { key: "pay_id", label: "Payment ID" }, { key: "pay_source", label: "Source" }] },
  health: { label: "Health bookings", map: (r) => ({ ...r, date: r.created, ...pay(r.pay) }),
    cols: [{ key: "date", label: "Date" }, { key: "no", label: "No" }, { key: "kind", label: "Type" }, { key: "status", label: "Status" },
      { key: "pay_state", label: "Payment" }, { key: "amount", label: "Amount", align: "right", render: (r) => inr(r.amount) }, ...payCols] },
};

export function CustomerStatement({ customerId, onClose }) {
  const [d, setD] = useState(null);
  const [tab, setTab] = useState("service_requests");
  useEffect(() => {
    api.get(`/finance/invoice-analytics/customers/${customerId}/statement`)
      .then(r => setD(r.data)).catch(e => { toast.error(formatApiError(e)); onClose(); });
  }, [customerId]); // eslint-disable-line react-hooks/exhaustive-deps

  if (!d) return null;
  const t = TABS[tab];
  const rows = (d[tab] || []).map(t.map);
  const fname = `statement_${(d.customer.code || d.customer.name || "customer").replace(/\s+/g, "-")}_${tab}`;
  const csv = () => saveBlob(rowsToCsv(t.cols.map(c => c.label), rows.map(r => t.cols.map(c => cellText(c, r)))), `${fname}.csv`);
  const pdf = () => plainPdf({ title: `Statement ${d.customer.name} - ${t.label}`, columns: t.cols, rows,
    f: { from: "lifetime", to: new Date().toISOString().slice(0, 10), q: d.customer.phone } });

  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex justify-end" data-testid="ia-statement" onClick={onClose}>
      <div className="w-full max-w-4xl h-full bg-[#FAF9F6] overflow-y-auto p-5 space-y-4 shadow-2xl" onClick={e => e.stopPropagation()}>
        <div className="flex items-start justify-between gap-3">
          <div>
            <div className="text-[10px] uppercase tracking-[0.2em] text-[#8A8780]">Customer statement</div>
            <h2 className="font-heading text-2xl" data-testid="ia-statement-name">{d.customer.name}</h2>
            <div className="text-xs text-[#5C5A55] font-mono">{d.customer.code || "—"} · {d.customer.phone || "—"}</div>
          </div>
          <button onClick={onClose} data-testid="ia-statement-close" className="p-2 rounded hover:bg-white"><X size={16}/></button>
        </div>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
          {[["Wallet", d.customer.wallet_balance], ["Lifetime billed", d.lifetime.billed], ["Lifetime paid", d.lifetime.paid], ["Lifetime due", d.lifetime.due]].map(([l, v]) => (
            <div key={l} className="bg-white border border-[#E5E2D9] rounded-md p-3" data-testid={`ia-statement-${l.split(" ").pop().toLowerCase()}`}>
              <div className="text-[10px] uppercase tracking-[0.16em] text-[#8A8780]">{l}</div>
              <div className={`font-heading text-lg ${l === "Lifetime due" && v > 0 ? "text-[#B42828]" : ""}`}>{inr(v)}</div>
            </div>
          ))}
        </div>
        <div className="flex items-center gap-1 flex-wrap border-b border-[#E5E2D9]">
          {Object.entries(TABS).map(([k, v]) => (
            <button key={k} onClick={() => setTab(k)} data-testid={`ia-statement-tab-${k}`}
                    className={`px-3 py-2 text-xs font-medium border-b-2 ${tab === k ? "border-[#D23247] text-[#D23247]" : "border-transparent text-[#5C5A55]"}`}>
              {v.label} <span className="opacity-60">{(d[k] || []).length}</span>
            </button>
          ))}
          <div className="ml-auto flex gap-1.5 pb-1">
            <button onClick={csv} data-testid="ia-statement-csv" className="h-8 px-3 rounded-md border border-[#E5E2D9] bg-white text-xs inline-flex items-center gap-1"><FileCsv size={13}/>CSV</button>
            <button onClick={pdf} data-testid="ia-statement-pdf" className="h-8 px-3 rounded-md border border-[#E5E2D9] bg-white text-xs inline-flex items-center gap-1"><FilePdf size={13}/>PDF</button>
          </div>
        </div>
        <SortableTable testid={`ia-statement-${tab}`} columns={t.cols} rows={rows} rowKey={r => r.id || `${r.no}-${r.date}`}
                       initialSort={{ key: "date", dir: "desc" }}/>
      </div>
    </div>
  );
}
